import { useEffect, useMemo, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { timelinePhases, timelineHighlights } from '../data/TimelineData';
import { trackEvent } from '../utils/analytics';
import SEO from '../components/SEO';

const PageContainer = styled.div`
  padding: 4rem 3rem 5rem;
  max-width: 1400px;
  margin: 0 auto;

  @media (max-width: 1024px) {
    padding: 3rem 1.75rem 4rem;
  }

  @media (max-width: 600px) {
    padding: 2.5rem 1.25rem 3rem;
  }
`;

const HeroSection = styled.section`
  background: ${({ theme }) => theme.colors.paper};
  border: 1.5px solid ${({ theme }) => theme.colors.brown};
  padding: 3rem;
  box-shadow:
    inset 0 1px 3px rgba(0, 0, 0, 0.05),
    2px 2px 12px rgba(0, 0, 0, 0.08);
  border-radius: 4px;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 2rem;
  }

  @media (max-width: 500px) {
    padding: 1.5rem;
  }
`;

const HeroBackdrop = styled.div`
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at 20% 20%, rgba(212,132,124,0.1), transparent 55%),
    linear-gradient(135deg, rgba(196,181,160,0.18), transparent 60%);
  pointer-events: none;
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
`;

const HeroEyebrow = styled.p`
  font-family: ${({ theme }) => theme.fonts.mono};
  letter-spacing: 0.4em;
  text-transform: uppercase;
  font-size: 0.8rem;
  margin: 0 0 1rem;
  color: ${({ theme }) => theme.colors.brownDark};
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  text-transform: uppercase;
  letter-spacing: 3px;
  margin: 0 0 1.25rem;
  color: ${({ theme }) => theme.colors.ink};

  @media (max-width: 768px) {
    font-size: 2.4rem;
  }

  @media (max-width: 500px) {
    font-size: 2rem;
    letter-spacing: 2px;
  }
`;

const HeroLead = styled.p`
  font-size: 1.1rem;
  line-height: 1.7;
  font-family: ${({ theme }) => theme.fonts.mono};
  color: ${({ theme }) => theme.colors.ink};
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const CTAGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 2.5rem;
`;

const CTAButton = styled.button`
  border: 1.5px solid ${({ theme }) => theme.colors.brownDark};
  background: ${({ $variant, theme }) =>
    $variant === 'primary' ? theme.colors.ink : theme.colors.paper};
  color: ${({ $variant, theme }) =>
    $variant === 'primary' ? theme.colors.paper : theme.colors.ink};
  text-transform: uppercase;
  letter-spacing: 0.2em;
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 0.8rem;
  padding: 0.95rem 1.75rem;
  border-radius: 3px;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 14px rgba(0, 0, 0, 0.1);
  }

  @media (max-width: 500px) {
    flex: 1 0 100%;
    text-align: center;
  }
`;

const HeroStatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`;

const HeroStatCard = styled.div`
  border: 1px dashed ${({ theme }) => theme.colors.brown};
  padding: 1rem 1.25rem;
  background: rgba(255, 254, 249, 0.8);
  backdrop-filter: blur(2px);
  min-height: 120px;

  @media (max-width: 600px) {
    padding: 0.875rem 1rem;
    min-height: auto;
  }
`;

const HeroStatValue = styled.div`
  font-size: 1.8rem;
  font-family: ${({ theme }) => theme.fonts.mono};
  color: ${({ theme }) => theme.colors.ink};

  @media (max-width: 600px) {
    font-size: 1.5rem;
  }
`;

const HeroStatLabel = styled.div`
  text-transform: uppercase;
  letter-spacing: 0.2em;
  font-size: 0.7rem;
  color: ${({ theme }) => theme.colors.brownDark};
  margin-top: 0.35rem;
`;

const HeroStatDescription = styled.p`
  margin: 0.5rem 0 0;
  font-size: 0.85rem;
  font-family: ${({ theme }) => theme.fonts.mono};
  color: ${({ theme }) => theme.colors.ink};
`;

const ProgressWrapper = styled.div`
  margin: 2.5rem 0 1.75rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;

  @media (max-width: 600px) {
    margin: 2rem 0 1.5rem;
    gap: 0.75rem;
  }
`;

const ProgressLabel = styled.span`
  font-family: ${({ theme }) => theme.fonts.mono};
  text-transform: uppercase;
  letter-spacing: 0.3em;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.brownDark};

  @media (max-width: 600px) {
    font-size: 0.7rem;
    letter-spacing: 0.2em;
  }
`;

const ProgressTrack = styled.div`
  flex: 1 1 300px;
  height: 6px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.manilaDark};
  position: relative;
  overflow: hidden;

  @media (max-width: 600px) {
    flex: 1 1 200px;
    min-width: 0;
  }
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${({ $value }) => `${$value}%`};
  background: linear-gradient(90deg, #d4847c, #c4b5a0);
  border-radius: inherit;
  transition: width 0.2s ease;
`;

const ProgressValue = styled.span`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.ink};
  min-width: 60px;
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: 260px minmax(0, 1fr);
  gap: 2rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const EraRail = styled.aside`
  position: sticky;
  top: 120px;
  align-self: start;
  border: 1px solid ${({ theme }) => theme.colors.brown};
  background: ${({ theme }) => theme.colors.paper};
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.05);
  padding: 1.5rem 1rem;
  border-radius: 4px;

  @media (max-width: 1024px) {
    position: static;
    display: flex;
    overflow-x: auto;
    gap: 0.5rem;
    border-radius: 999px;
    padding: 0.75rem;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin;
    
    &::-webkit-scrollbar {
      height: 4px;
    }
    
    &::-webkit-scrollbar-track {
      background: ${({ theme }) => theme.colors.manilaDark};
      border-radius: 2px;
    }
    
    &::-webkit-scrollbar-thumb {
      background: ${({ theme }) => theme.colors.brown};
      border-radius: 2px;
    }
  }
`;

const EraRailTitle = styled.p`
  font-family: ${({ theme }) => theme.fonts.mono};
  text-transform: uppercase;
  letter-spacing: 0.3em;
  font-size: 0.7rem;
  color: ${({ theme }) => theme.colors.brownDark};
  margin: 0 0 1rem;

  @media (max-width: 1024px) {
    display: none;
  }
`;

const EraList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  @media (max-width: 1024px) {
    flex-direction: row;
    gap: 0.5rem;
  }
`;

const EraButton = styled.button`
  width: 100%;
  border: 1px solid ${({ theme }) => theme.colors.brown};
  background: ${({ $active, theme }) =>
    $active ? theme.colors.manila : theme.colors.paper};
  color: ${({ theme }) => theme.colors.ink};
  font-family: ${({ theme }) => theme.fonts.mono};
  text-align: left;
  padding: 0.9rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.2s ease;

  ${({ $active }) =>
    $active
      ? `
    box-shadow: 0 6px 14px rgba(0,0,0,0.1);
    transform: translateX(6px);
  `
      : `
    &:hover {
      background: #f8f2e6;
    }
  `}

  @media (max-width: 1024px) {
    min-width: 200px;
    text-align: center;
    transform: none;
    padding: 0.75rem 1rem;
  }

  @media (max-width: 600px) {
    min-width: 160px;
    padding: 0.65rem 0.85rem;
  }
`;

const EraButtonTitle = styled.div`
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;

  @media (max-width: 600px) {
    font-size: 0.8rem;
  }
`;

const EraButtonPeriod = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.brownDark};
`;

const TimelineColumn = styled.div`
  position: relative;
  padding-left: 2rem;

  @media (max-width: 900px) {
    padding-left: 0;
  }
`;

const TimelineSpine = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 40px;
  width: 2px;
  background: linear-gradient(
    to bottom,
    rgba(90, 82, 71, 0),
    rgba(90, 82, 71, 0.4),
    rgba(90, 82, 71, 0)
  );

  @media (max-width: 900px) {
    display: none;
  }
`;

const PhaseSection = styled.section`
  margin-bottom: 4rem;
  padding-left: 2.5rem;
  position: relative;

  @media (max-width: 900px) {
    padding-left: 0;
    margin-bottom: 3rem;
  }

  @media (max-width: 600px) {
    margin-bottom: 2.5rem;
  }
`;

const PhaseMarker = styled.div`
  position: absolute;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 3px solid ${({ theme }) => theme.colors.brown};
  background: ${({ $active, theme }) =>
    $active ? theme.colors.brown : theme.colors.paper};
  left: 9px;
  top: 6px;
  transform: translate(-50%, 0);
  transition: background 0.2s ease, transform 0.2s ease;

  @media (max-width: 900px) {
    display: none;
  }
`;

const PhaseHeader = styled.div`
  background: ${({ theme }) => theme.colors.paper};
  border: 1px solid ${({ theme }) => theme.colors.brown};
  padding: 1.5rem 2rem;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.7),
    1px 1px 6px rgba(0, 0, 0, 0.08);
  border-radius: 4px;
  margin-bottom: 1.5rem;

  @media (max-width: 600px) {
    padding: 1.25rem 1.5rem;
  }
`;

const PhaseTitle = styled.h2`
  margin: 0 0 0.5rem;
  font-size: 1.8rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.ink};

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }

  @media (max-width: 600px) {
    font-size: 1.3rem;
    letter-spacing: 0.08em;
  }
`;

const PhasePeriod = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.fonts.mono};
  text-transform: uppercase;
  letter-spacing: 0.2em;
  color: ${({ theme }) => theme.colors.brownDark};
  font-size: 0.8rem;

  @media (max-width: 600px) {
    font-size: 0.75rem;
  }
`;

const PhaseTheme = styled.p`
  margin: 0.75rem 0 0;
  font-style: italic;
  font-family: ${({ theme }) => theme.fonts.mono};
  color: ${({ theme }) => theme.colors.ink};

  @media (max-width: 600px) {
    font-size: 0.9rem;
    margin: 0.5rem 0 0;
  }
`;

const PhaseSummary = styled.p`
  font-family: ${({ theme }) => theme.fonts.mono};
  line-height: 1.8;
  color: ${({ theme }) => theme.colors.ink};
  margin: 0 0 1.5rem;

  @media (max-width: 600px) {
    font-size: 0.95rem;
    line-height: 1.7;
    margin: 0 0 1.25rem;
  }
`;

const PhaseMetaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
    margin-bottom: 1.5rem;
  }
`;

const PhaseMetaCard = styled.div`
  border: 1px dashed ${({ theme }) => theme.colors.brown};
  padding: 1rem 1.25rem;
  background: ${({ theme }) => theme.colors.paper};
  border-radius: 4px;

  @media (max-width: 600px) {
    padding: 0.875rem 1rem;
  }
`;

const MetaLabel = styled.div`
  text-transform: uppercase;
  letter-spacing: 0.2em;
  font-size: 0.7rem;
  color: ${({ theme }) => theme.colors.brownDark};
  margin-bottom: 0.3rem;
`;

const MetaValue = styled.div`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.ink};
`;

const SpotlightCard = styled.div`
  border: 1.5px solid ${({ theme }) => theme.colors.red};
  padding: 1rem 1.25rem;
  border-radius: 4px;
  background: rgba(212, 132, 124, 0.08);
  margin-bottom: 2.5rem;

  @media (max-width: 600px) {
    padding: 0.875rem 1rem;
    margin-bottom: 2rem;
  }
`;

const SpotlightLabel = styled.div`
  font-family: ${({ theme }) => theme.fonts.mono};
  text-transform: uppercase;
  letter-spacing: 0.2em;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.red};
  margin-bottom: 0.5rem;
`;

const SpotlightValue = styled.p`
  margin: 0;
  font-size: 1rem;
  font-family: ${({ theme }) => theme.fonts.mono};
  color: ${({ theme }) => theme.colors.ink};
  line-height: 1.6;

  @media (max-width: 600px) {
    font-size: 0.95rem;
  }
`;

const EventsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  position: relative;

  @media (max-width: 600px) {
    gap: 1.5rem;
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(24px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const EventCard = styled.article`
  width: min(520px, 85%);
  align-self: ${({ $align }) =>
    $align === 'left' ? 'flex-start' : 'flex-end'};
  background: ${({ theme }) => theme.colors.paper};
  border: 1px solid ${({ theme }) => theme.colors.brown};
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.7),
    2px 2px 10px rgba(0, 0, 0, 0.08);
  padding: 1.75rem;
  border-radius: 4px;
  position: relative;
  animation: ${fadeIn} 0.5s ease forwards;
  animation-delay: ${({ $delay }) => `${$delay}s`};
  opacity: 0;

  &::before {
    content: '';
    position: absolute;
    width: 18px;
    height: 2px;
    background: ${({ theme }) => theme.colors.brown};
    top: 20px;
    ${({ $align }) =>
      $align === 'left'
        ? `
        left: -34px;
      `
        : `
        right: -34px;
      `}
  }

  @media (max-width: 900px) {
    width: 100%;
    align-self: stretch;

    &::before {
      display: none;
    }
  }

  @media (max-width: 600px) {
    padding: 1.25rem 1.5rem;
  }

  @media (max-width: 400px) {
    padding: 1rem 1.25rem;
  }
`;

const EventYear = styled.span`
  font-family: ${({ theme }) => theme.fonts.mono};
  text-transform: uppercase;
  letter-spacing: 0.3em;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.brownDark};
`;

const EventHeadline = styled.h3`
  margin: 0.35rem 0 0.75rem;
  font-size: 1.3rem;
  color: ${({ theme }) => theme.colors.ink};
  text-transform: uppercase;
  letter-spacing: 0.08em;

  @media (max-width: 768px) {
    font-size: 1.15rem;
  }

  @media (max-width: 600px) {
    font-size: 1.05rem;
    letter-spacing: 0.06em;
    margin: 0.35rem 0 0.65rem;
  }
`;

const EventBody = styled.p`
  margin: 0 0 0.75rem;
  font-family: ${({ theme }) => theme.fonts.mono};
  line-height: 1.7;

  @media (max-width: 600px) {
    font-size: 0.95rem;
    line-height: 1.65;
    margin: 0 0 0.65rem;
  }
`;

const EventQuote = styled.blockquote`
  margin: 0;
  padding: 0.5rem 0 0 1rem;
  border-left: 3px solid ${({ theme }) => theme.colors.red};
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.red};

  @media (max-width: 600px) {
    font-size: 0.85rem;
    padding: 0.5rem 0 0 0.75rem;
  }
`;

const MediaPanel = styled.div`
  margin-top: 1rem;
  border: 1px dashed ${({ theme }) => theme.colors.brown};
  padding: 0.75rem;
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.manilaLight};

  @media (max-width: 600px) {
    padding: 0.65rem;
    margin-top: 0.875rem;
  }
`;

const MediaImage = styled.img`
  width: 100%;
  border-radius: 3px;
  border: 1px solid ${({ theme }) => theme.colors.brown};
  object-fit: cover;
`;

const MediaCaption = styled.p`
  font-size: 0.8rem;
  margin: 0.5rem 0 0;
  color: ${({ theme }) => theme.colors.ink};
  font-family: ${({ theme }) => theme.fonts.mono};

  @media (max-width: 600px) {
    font-size: 0.75rem;
    margin: 0.4rem 0 0;
  }
`;

const EventActions = styled.div`
  margin-top: 1rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;

  @media (max-width: 600px) {
    margin-top: 0.875rem;
    gap: 0.4rem;
  }
`;

const EventActionButton = styled.button`
  border: 1px solid ${({ theme }) => theme.colors.brownDark};
  background: transparent;
  color: ${({ theme }) => theme.colors.ink};
  font-family: ${({ theme }) => theme.fonts.mono};
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-size: 0.7rem;
  padding: 0.4rem 0.75rem;
  border-radius: 3px;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.manila};
  }

  @media (max-width: 600px) {
    font-size: 0.65rem;
    padding: 0.35rem 0.65rem;
  }
`;

const RelatedDocs = styled.div`
  margin-top: 2.5rem;
  border-top: 1px dashed ${({ theme }) => theme.colors.brown};
  padding-top: 1.5rem;

  @media (max-width: 600px) {
    margin-top: 2rem;
    padding-top: 1.25rem;
  }
`;

const RelatedDocsTitle = styled.h4`
  margin: 0 0 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.2em;
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colors.brownDark};
`;

const DocButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;

  @media (max-width: 600px) {
    gap: 0.4rem;
  }
`;

const DocButton = styled.button`
  border: 1px dashed ${({ theme }) => theme.colors.brown};
  background: ${({ theme }) => theme.colors.paper};
  color: ${({ theme }) => theme.colors.ink};
  font-family: ${({ theme }) => theme.fonts.mono};
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-size: 0.65rem;
  padding: 0.5rem 0.9rem;
  border-radius: 999px;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.ink};
    color: ${({ theme }) => theme.colors.paper};
  }

  @media (max-width: 600px) {
    font-size: 0.6rem;
    padding: 0.45rem 0.75rem;
  }
`;

function Timeline() {
  const navigate = useNavigate();
  const [activePhaseId, setActivePhaseId] = useState(
    () => timelinePhases[0]?.id || null
  );
  const [scrollProgress, setScrollProgress] = useState(0);
  const phaseRefs = useRef({});
  const timelineRef = useRef(null);
  const eraButtonRefs = useRef({});
  const eraRailRef = useRef(null);

  const orderedPhases = useMemo(() => timelinePhases, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        // Find all intersecting entries
        const intersecting = entries.filter((entry) => entry.isIntersecting);
        
        if (intersecting.length > 0) {
          // Sort by intersection ratio (most visible first) and then by position
          const sorted = intersecting.sort((a, b) => {
            // Prefer higher intersection ratio
            if (Math.abs(b.intersectionRatio - a.intersectionRatio) > 0.1) {
              return b.intersectionRatio - a.intersectionRatio;
            }
            // If ratios are similar, prefer the one higher on the page
            return a.boundingClientRect.top - b.boundingClientRect.top;
          });
          
          const newActiveId = sorted[0].target.dataset.phaseId;
          if (newActiveId) {
            setActivePhaseId(newActiveId);
          }
        }
      },
      { 
        rootMargin: '-30% 0px -50% 0px', 
        threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5]
      }
    );

    // Use a small delay to ensure refs are set
    const timeoutId = setTimeout(() => {
      orderedPhases.forEach((phase) => {
        const node = phaseRefs.current[phase.id];
        if (node) {
          observer.observe(node);
        }
      });
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [orderedPhases]);

  // Auto-scroll active chapter button into view in the chapters list
  useEffect(() => {
    if (!activePhaseId) return;

    const activeButton = eraButtonRefs.current[activePhaseId];
    const railContainer = eraRailRef.current;

    if (activeButton && railContainer) {
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        const isHorizontal = getComputedStyle(railContainer).overflowX === 'auto';

        if (isHorizontal) {
          // On mobile/horizontal layout, scroll only the rail container
          const offsetLeft = activeButton.getBoundingClientRect().left - railContainer.getBoundingClientRect().left;
          const currentScroll = railContainer.scrollLeft;
          const buttonPositionInContent = offsetLeft + currentScroll;
          const targetScrollLeft = buttonPositionInContent - (railContainer.clientWidth / 2) + (activeButton.offsetWidth / 2);

          railContainer.scrollTo({
            left: targetScrollLeft,
            behavior: 'smooth'
          });
        } else {
          // On desktop, verify if scroll is needed
          const buttonRect = activeButton.getBoundingClientRect();
          const railRect = railContainer.getBoundingClientRect();

          const isOutsideLeft = buttonRect.left < railRect.left;
          const isOutsideRight = buttonRect.right > railRect.right;
          const isOutsideTop = buttonRect.top < railRect.top;
          const isOutsideBottom = buttonRect.bottom > railRect.bottom;

          if (isOutsideLeft || isOutsideRight || isOutsideTop || isOutsideBottom) {
            activeButton.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
              inline: 'center'
            });
          }
        }
      });
    }
  }, [activePhaseId]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const updateProgress = () => {
      const el = timelineRef.current;
      if (!el) {
        return;
      }
      const rectTop = el.getBoundingClientRect().top + window.scrollY;
      const height = el.offsetHeight;
      const viewport = window.innerHeight;
      const max = Math.max(height - viewport, 1);
      const scrolled = Math.min(Math.max(window.scrollY - rectTop, 0), max);
      const value = Math.round((scrolled / max) * 100);
      setScrollProgress(value);

      // Fallback: Find the phase closest to viewport top (for when IntersectionObserver misses)
      const viewportTop = window.scrollY + 200; // Offset for header
      let closestPhase = null;
      let closestDistance = Infinity;

      orderedPhases.forEach((phase) => {
        const node = phaseRefs.current[phase.id];
        if (node) {
          const rect = node.getBoundingClientRect();
          const elementTop = rect.top + window.scrollY;
          
          // Check if element is in viewport
          if (rect.top < window.innerHeight && rect.bottom > 0) {
            // Find the phase whose top is closest to but above the viewport top
            const distance = Math.abs(viewportTop - elementTop);
            if (distance < closestDistance) {
              closestDistance = distance;
              closestPhase = phase.id;
            }
          }
        }
      });

      // Update active phase if we found one and it's reasonably close
      if (closestPhase && closestDistance < window.innerHeight) {
        setActivePhaseId((current) => {
          // Only update if it's actually different to avoid unnecessary re-renders
          return current !== closestPhase ? closestPhase : current;
        });
      }
    };

    updateProgress();
    window.addEventListener('scroll', updateProgress, { passive: true });
    window.addEventListener('resize', updateProgress);
    return () => {
      window.removeEventListener('scroll', updateProgress);
      window.removeEventListener('resize', updateProgress);
    };
  }, [orderedPhases]);

  const handleEraClick = (phaseId) => {
    const node = phaseRefs.current[phaseId];
    if (!node) {
      return;
    }
    // Immediately update active phase when clicking
    setActivePhaseId(phaseId);
    trackEvent('timeline_jump', 'timeline', phaseId);
    node.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleSearchNavigate = (query) => {
    trackEvent('timeline_cta', 'timeline', 'search');
    const target = query ? `/?q=${encodeURIComponent(query)}` : '/';
    navigate(target);
  };

  const handleExplorerNavigate = () => {
    trackEvent('timeline_cta', 'timeline', 'explorer');
    navigate('/explorer');
  };

  const handleEvidenceClick = (item, sourceId) => {
    if (item.documentSlug) {
      trackEvent('timeline_event_doc', 'timeline', sourceId || item.id);
      navigate(`/document/${item.documentSlug}`);
    } else if (item.searchQuery) {
      trackEvent('timeline_event_search', 'timeline', sourceId || item.id);
      navigate(`/?q=${encodeURIComponent(item.searchQuery)}`);
    }
  };

  return (
    <PageContainer>
      <SEO 
        title="Timeline" 
        description="Chronological timeline of events related to Jeffrey Epstein, with links to primary source documents."
      />
      <HeroSection>
        <HeroBackdrop />
        <HeroContent>
          <HeroEyebrow>Context Briefing</HeroEyebrow>
          <HeroTitle>The Epstein Timeline</HeroTitle>
          <HeroLead>
            Follow the decisions, alleged enablers, and survivor testimony that explain how
            Jeffrey Epstein built, protected, and ultimately lost his alleged trafficking
            empire. Each chapter links directly into the primary sources inside this
            archive.
          </HeroLead>
          <CTAGroup>
            <CTAButton
              $variant="primary"
              onClick={() => handleSearchNavigate('Palm Beach police affidavit')}
            >
              Start Searching
            </CTAButton>
            <CTAButton onClick={handleExplorerNavigate}>Open Explorer</CTAButton>
          </CTAGroup>
          <HeroStatsGrid>
            {timelineHighlights.map((stat) => (
              <HeroStatCard key={stat.label}>
                <HeroStatValue>{stat.value}</HeroStatValue>
                <HeroStatLabel>{stat.label}</HeroStatLabel>
                <HeroStatDescription>{stat.description}</HeroStatDescription>
              </HeroStatCard>
            ))}
          </HeroStatsGrid>
        </HeroContent>
      </HeroSection>

      <ProgressWrapper>
        <ProgressLabel>Story Progress</ProgressLabel>
        <ProgressTrack>
          <ProgressFill $value={scrollProgress} />
        </ProgressTrack>
        <ProgressValue>{scrollProgress}%</ProgressValue>
      </ProgressWrapper>

      <Layout ref={timelineRef}>
        <EraRail ref={eraRailRef}>
          <EraRailTitle>Chapters</EraRailTitle>
          <EraList>
            {orderedPhases.map((phase) => (
              <EraButton
                key={phase.id}
                ref={(node) => {
                  if (node) {
                    eraButtonRefs.current[phase.id] = node;
                  }
                }}
                type="button"
                onClick={() => handleEraClick(phase.id)}
                $active={phase.id === activePhaseId}
              >
                <EraButtonTitle>{phase.title}</EraButtonTitle>
                <EraButtonPeriod>{phase.period}</EraButtonPeriod>
              </EraButton>
            ))}
          </EraList>
        </EraRail>

        <TimelineColumn>
          <TimelineSpine aria-hidden="true" />
          {orderedPhases.map((phase) => (
            <PhaseSection
              key={phase.id}
              ref={(node) => {
                if (node) {
                  phaseRefs.current[phase.id] = node;
                }
              }}
              data-phase-id={phase.id}
            >
              <PhaseMarker $active={phase.id === activePhaseId} />
              <PhaseHeader>
                <PhaseTitle>{phase.title}</PhaseTitle>
                <PhasePeriod>{phase.period}</PhasePeriod>
                <PhaseTheme>{phase.theme}</PhaseTheme>
              </PhaseHeader>
              <PhaseSummary>{phase.summary}</PhaseSummary>
              <PhaseMetaGrid>
                {phase.stats?.map((stat) => (
                  <PhaseMetaCard key={stat.label}>
                    <MetaLabel>{stat.label}</MetaLabel>
                    <MetaValue>{stat.value}</MetaValue>
                  </PhaseMetaCard>
                ))}
              </PhaseMetaGrid>
              {phase.spotlight && (
                <SpotlightCard>
                  <SpotlightLabel>{phase.spotlight.label}</SpotlightLabel>
                  <SpotlightValue>{phase.spotlight.value}</SpotlightValue>
                </SpotlightCard>
              )}
              <EventsList>
                {phase.events.map((event, index) => {
                  const align = index % 2 === 0 ? 'left' : 'right';
                  return (
                    <EventCard
                      key={event.id}
                      $align={align}
                      $delay={index * 0.08}
                    >
                      <EventYear>{event.year}</EventYear>
                      <EventHeadline>{event.headline}</EventHeadline>
                      <EventBody>{event.body}</EventBody>
                      {event.quote && <EventQuote>{event.quote}</EventQuote>}
                      {event.media?.type === 'image' && event.media.src && (
                        <MediaPanel>
                          <MediaImage
                            src={event.media.src}
                            alt={event.media.alt || event.headline}
                            loading="lazy"
                          />
                          {event.media.caption && (
                            <MediaCaption>{event.media.caption}</MediaCaption>
                          )}
                        </MediaPanel>
                      )}
                      {(event.searchQuery || event.documentSlug) && (
                        <EventActions>
                          <EventActionButton
                            type="button"
                            onClick={() => handleEvidenceClick(event)}
                          >
                            {event.documentSlug ? 'Open document' : 'Search sources'}
                          </EventActionButton>
                        </EventActions>
                      )}
                    </EventCard>
                  );
                })}
              </EventsList>
              {phase.relatedDocs?.length > 0 && (
                <RelatedDocs>
                  <RelatedDocsTitle>Primary Sources</RelatedDocsTitle>
                  <DocButtonRow>
                    {phase.relatedDocs.map((doc) => (
                      <DocButton
                        key={doc.label}
                        type="button"
                        onClick={() => handleEvidenceClick(doc, phase.id)}
                      >
                        {doc.label}
                      </DocButton>
                    ))}
                  </DocButtonRow>
                </RelatedDocs>
              )}
            </PhaseSection>
          ))}
        </TimelineColumn>
      </Layout>
    </PageContainer>
  );
}

export default Timeline;


